import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, ArrowRight, Clock, MapPin, Info } from "lucide-react";
import { api, MARKET_META } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import {
    SEO_MARKETS_BY_KEY,
    buildResultTodayCopy,
    resultTodayPath,
    commonNumberPath,
    previousResultPath,
} from "../lib/seoMarkets";

const SITE_URL = "https://shillongmorningteerresulttoday.com";

// Teer gaming day rollover — matches Home.jsx.
const todayIST = () =>
    new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata",
    });

const prettyDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const numDisplay = (v) => {
    if (v === null || v === undefined || v === "") return "XX";
    return String(v).padStart(2, "0");
};

const GameResultToday = ({ marketKey }) => {
    const meta = SEO_MARKETS_BY_KEY[marketKey];
    const cardMeta = MARKET_META[marketKey];
    const copy = useMemo(() => buildResultTodayCopy(meta), [meta]);
    const slug = meta.urlPrefix;
    const path = resultTodayPath(slug);

    useSeo({
        title: copy.title,
        description: copy.description,
        keywords: copy.keywords,
        path,
        image: `${SITE_URL}/og-image.png`,
        imageAlt: `${meta.display} Result Today – Live FR and SR Results`,
        breadcrumbs: [
            { name: meta.display, path },
            { name: "Result Today", path },
        ],
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: copy.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    });

    const [today, setToday] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);
    const dateStr = todayIST();

    const load = async () => {
        setLoading(true);
        try {
            const [tRes, rRes] = await Promise.all([
                api.get("/results/today"),
                api.get("/results", { params: { market_key: marketKey, limit: 10 } }),
            ]);
            const mine = tRes.data.find((r) => r.market_key === marketKey);
            setToday(mine || null);
            // rRes returns newest-first; skip today's row from recent list
            setRecent(rRes.data.filter((r) => r.date !== dateStr));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketKey]);

    const fr = numDisplay(today?.fr);
    const sr = numDisplay(today?.sr);
    const isFrPublished = today?.fr !== null && today?.fr !== undefined && today?.fr !== "";
    const isSrPublished = today?.sr !== null && today?.sr !== undefined && today?.sr !== "";
    const bothDone = isFrPublished && isSrPublished;

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-8" data-testid={`page-result-${slug}`}>
            <Breadcrumbs
                items={[
                    { name: meta.display, path },
                    { name: "Result Today", path },
                ]}
            />

            {/* Hero */}
            <header className="mt-4 rounded-2xl overflow-hidden card-crisp">
                <div
                    className="px-5 py-4 md:px-8 md:py-6 text-white"
                    style={{ background: cardMeta?.accent || "#1E3A8A" }}
                >
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-3xl leading-none" aria-hidden="true">
                            {cardMeta?.emoji || "🎯"}
                        </span>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest opacity-90 font-semibold">
                                Live Result · {prettyDate(dateStr)}
                            </div>
                            <h1
                                className="font-display font-bold text-2xl sm:text-3xl md:text-4xl tracking-tight"
                                data-testid="page-h1"
                            >
                                {copy.h1}
                            </h1>
                        </div>
                    </div>
                </div>

                {/* Numbers row */}
                <div className="grid grid-cols-2 divide-x divide-slate-200 bg-white">
                    <div className="p-5 md:p-7 text-center">
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                            F/R ({meta.frTime})
                        </div>
                        <div
                            className={`result-digit text-5xl md:text-7xl mt-2 ${
                                isFrPublished ? "text-blue-900" : "text-slate-300"
                            }`}
                            data-testid="result-fr"
                        >
                            {fr}
                        </div>
                        <div className="mt-1 text-[11px] uppercase tracking-widest font-semibold">
                            {isFrPublished ? (
                                <span className="text-emerald-700">Published</span>
                            ) : (
                                <span className="text-slate-400">Awaiting</span>
                            )}
                        </div>
                    </div>
                    <div className="p-5 md:p-7 text-center">
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                            S/R ({meta.srTime})
                        </div>
                        <div
                            className={`result-digit text-5xl md:text-7xl mt-2 ${
                                isSrPublished ? "text-blue-900" : "text-slate-300"
                            }`}
                            data-testid="result-sr"
                        >
                            {sr}
                        </div>
                        <div className="mt-1 text-[11px] uppercase tracking-widest font-semibold">
                            {isSrPublished ? (
                                <span className="text-emerald-700">Published</span>
                            ) : (
                                <span className="text-slate-400">Awaiting</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between bg-slate-50 border-t border-slate-200 px-4 py-2">
                    <div className="text-[11px] text-slate-500">
                        {today?.updated_at
                            ? `Last update · ${new Date(today.updated_at).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                              })}`
                            : "Awaiting today's declaration"}
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-900 hover:text-blue-700 disabled:opacity-50"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>
            </header>

            {/* Meta strip */}
            <section className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <InfoTile icon={<Clock className="h-4 w-4" />} label="F/R Time" value={meta.frTime} />
                <InfoTile icon={<Clock className="h-4 w-4" />} label="S/R Time" value={meta.srTime} />
                <InfoTile icon={<MapPin className="h-4 w-4" />} label="Location" value={meta.location} />
            </section>

            {/* About */}
            <section className="mt-8">
                <h2 className="font-display font-bold text-xl md:text-2xl text-blue-950 tracking-tight">
                    About {meta.display} Result
                </h2>
                <p className="text-slate-700 mt-2 leading-relaxed">{meta.blurb}</p>
                <p className="text-slate-700 mt-3 leading-relaxed">
                    On this page you'll always see the latest {meta.display} F/R and S/R numbers as soon as
                    they are announced. Result data auto-refreshes every 30 seconds so you don't have to
                    reload the page. If a round has not been declared yet the block will show{" "}
                    <em>Awaiting</em>.
                </p>
            </section>

            {/* Cross-links */}
            <section className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CrossLink
                    to={commonNumberPath(slug)}
                    title={`${meta.display} Common Number`}
                    desc="Direct · House · Ending — daily update"
                    testid="cta-common-number"
                />
                <CrossLink
                    to={previousResultPath(slug)}
                    title={`${meta.display} Previous Results`}
                    desc="FR & SR history archive"
                    testid="cta-previous-result"
                />
            </section>

            {/* Recent results */}
            <section className="mt-8">
                <h2 className="font-display font-bold text-xl md:text-2xl text-blue-950 tracking-tight">
                    Recent {meta.display} Results
                </h2>
                <div className="mt-3 card-crisp overflow-hidden">
                    <div className="grid grid-cols-12 bg-blue-900 text-white text-xs uppercase tracking-widest font-semibold px-4 py-2.5">
                        <div className="col-span-6 md:col-span-6">Date</div>
                        <div className="col-span-3 text-right">F/R</div>
                        <div className="col-span-3 text-right">S/R</div>
                    </div>
                    {loading ? (
                        <div className="p-4 text-center text-slate-500 text-sm">Loading…</div>
                    ) : recent.length === 0 ? (
                        <div className="p-6 text-center text-slate-400 text-sm" data-testid="recent-empty">
                            No previous {meta.display} records published yet.
                        </div>
                    ) : (
                        <ul>
                            {recent.slice(0, 8).map((r) => (
                                <li
                                    key={r.id}
                                    className="grid grid-cols-12 px-4 py-2 items-center border-t border-slate-100 hover:bg-slate-50"
                                    data-testid={`recent-row-${r.id}`}
                                >
                                    <div className="col-span-6 md:col-span-6 font-num text-sm text-slate-700">
                                        {prettyDate(r.date)}
                                    </div>
                                    <div className="col-span-3 text-right result-digit text-base text-blue-900">
                                        {r.fr ?? "XX"}
                                    </div>
                                    <div className="col-span-3 text-right result-digit text-base text-blue-900">
                                        {r.sr ?? "XX"}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="mt-3 text-right">
                    <Link
                        to={previousResultPath(slug)}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-blue-900 hover:underline"
                        data-testid="view-all-previous"
                    >
                        View full {meta.display} archive <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>

            {/* FAQ */}
            <section className="mt-10">
                <h2 className="font-display font-bold text-xl md:text-2xl text-blue-950 tracking-tight">
                    Frequently Asked Questions
                </h2>
                <div className="mt-3 space-y-3">
                    {copy.faq.map((f, i) => (
                        <details
                            key={i}
                            className="card-crisp p-4 open:shadow-md"
                            data-testid={`faq-${i}`}
                        >
                            <summary className="cursor-pointer font-semibold text-blue-950">
                                {f.q}
                            </summary>
                            <p className="mt-2 text-slate-700 leading-relaxed text-sm">{f.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* Disclaimer */}
            <div className="mt-8 rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-900 flex gap-3">
                <Info className="h-5 w-5 shrink-0 mt-0.5" />
                <p>
                    {meta.display} results shown here are for informational purposes only. Please follow
                    all local laws applicable to you.
                </p>
            </div>

            {/* Back to home */}
            <div className="mt-6 text-center">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-blue-900 hover:underline"
                    data-testid="back-home"
                >
                    ← Back to All Teer Results
                </Link>
            </div>
        </div>
    );
};

const InfoTile = ({ icon, label, value }) => (
    <div className="card-crisp p-3 flex items-center gap-3">
        <div className="h-9 w-9 rounded-md bg-blue-50 text-blue-900 grid place-items-center">
            {icon}
        </div>
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                {label}
            </div>
            <div className="text-sm font-semibold text-blue-950">{value}</div>
        </div>
    </div>
);

const CrossLink = ({ to, title, desc, testid }) => (
    <Link
        to={to}
        className="card-crisp p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid={testid}
    >
        <div>
            <div className="font-display font-bold text-blue-950 tracking-tight">{title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
        </div>
        <ArrowRight className="h-4 w-4 text-blue-900" />
    </Link>
);

export default GameResultToday;
